import type { FaqItem } from './services';

export type ServiceCityDeepContent = {
  serviceSlug: string;
  citySlug: string;
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  heroTitle: string;
  heroLead: string;
  introTitle: string;
  introParagraphs: string[];
  whyTitle: string;
  whyItems: string[];
  costLead: string;
  servicesTitle: string;
  servicesIntro: string;
  serviceItems: { title: string; body: string }[];
  timelineLead: string;
  processTitle: string;
  processSteps: { label: string; body: string }[];
  materialsTitle: string;
  materialsIntro: string;
  materials: string[];
  considerationsTitle: string;
  considerations: { title: string; body: string }[];
  areasTitle: string;
  areasIntro: string;
  areas: string[];
  nearby: { label: string; href: string }[];
  faqs: FaqItem[];
  ctaTitle: string;
  ctaLead: string;
};

type CityKey = 'austin' | 'dallas' | 'houston' | 'san-antonio';

const CITY_META: Record<
  CityKey,
  { name: string; region: string; nearby: { label: string; href: string }[] }
> = {
  austin: {
    name: 'Austin',
    region: 'Central Texas',
    nearby: [
      { label: 'Dallas bathroom remodeling', href: '/services/bathroom-remodeling/dallas' },
      { label: 'Houston bathroom remodeling', href: '/services/bathroom-remodeling/houston' },
      { label: 'San Antonio bathroom remodeling', href: '/services/bathroom-remodeling/san-antonio' },
      { label: 'Austin remodeling overview', href: '/locations/austin' },
      { label: 'Service Areas', href: '/service-areas' },
    ],
  },
  dallas: {
    name: 'Dallas',
    region: 'Dallas-Fort Worth',
    nearby: [
      { label: 'Fort Worth remodeling', href: '/locations/fort-worth' },
      { label: 'Arlington remodeling', href: '/locations/arlington' },
      { label: 'Austin bathroom remodeling', href: '/services/bathroom-remodeling/austin' },
      { label: 'Dallas remodeling overview', href: '/locations/dallas' },
      { label: 'Service Areas', href: '/service-areas' },
    ],
  },
  houston: {
    name: 'Houston',
    region: 'Greater Houston',
    nearby: [
      { label: 'The Woodlands remodeling', href: '/locations/the-woodlands' },
      { label: 'Sugar Land remodeling', href: '/locations/sugar-land' },
      { label: 'Pearland remodeling', href: '/locations/pearland' },
      { label: 'Houston remodeling overview', href: '/locations/houston' },
      { label: 'Service Areas', href: '/service-areas' },
    ],
  },
  'san-antonio': {
    name: 'San Antonio',
    region: 'South Texas',
    nearby: [
      { label: 'Austin bathroom remodeling', href: '/services/bathroom-remodeling/austin' },
      { label: 'Houston bathroom remodeling', href: '/services/bathroom-remodeling/houston' },
      { label: 'Dallas bathroom remodeling', href: '/services/bathroom-remodeling/dallas' },
      { label: 'San Antonio remodeling overview', href: '/locations/san-antonio' },
      { label: 'Service Areas', href: '/service-areas' },
    ],
  },
};

function swapNearby(serviceSlug: string, city: CityKey) {
  return CITY_META[city].nearby.map((n) => ({
    ...n,
    href: n.href.includes('/services/bathroom-remodeling/')
      ? n.href.replace('/services/bathroom-remodeling/', `/services/${serviceSlug}/`)
      : n.href,
    label: n.label.includes('bathroom remodeling')
      ? n.label.replace('bathroom remodeling', serviceLabel(serviceSlug))
      : n.label,
  }));
}

function serviceLabel(slug: string) {
  if (slug === 'adu-construction') return 'ADU construction';
  if (slug === 'outdoor-living') return 'outdoor living';
  return 'bathroom remodeling';
}

function bathCity(city: CityKey, unique: Partial<ServiceCityDeepContent> & Pick<
  ServiceCityDeepContent,
  | 'introParagraphs'
  | 'whyItems'
  | 'costLead'
  | 'timelineLead'
  | 'materials'
  | 'considerations'
  | 'areas'
  | 'faqs'
>): ServiceCityDeepContent {
  const meta = CITY_META[city];
  return {
    serviceSlug: 'bathroom-remodeling',
    citySlug: city,
    metaTitle: `Bathroom Remodeling in ${meta.name}, TX | 2026 Cost Guide | Epic Home Pros`,
    metaDescription: `${meta.name} bathroom remodeling with a 2026 Texas cost guide, waterproofing notes, timelines, and free on-site estimates.`,
    eyebrow: `${meta.name}, TX Bathroom Remodeling`,
    heroTitle: `Bathroom Remodeling in ${meta.name}, TX`,
    heroLead: `Walk-in showers, tub conversions, hall baths, and primary suites in ${meta.region} - plan cost tiers first, then get a free on-site estimate.`,
    introTitle: `Bathroom remodeling for ${meta.name} homes`,
    servicesTitle: `Bathroom remodeling services in ${meta.name}`,
    servicesIntro: 'Projects commonly include the categories below. Your written estimate defines what is in scope.',
    serviceItems: [
      {
        title: 'Walk-in showers and conversions',
        body: 'Tiled walk-ins, tub-to-shower conversions, and low-threshold options when the space allows.',
      },
      {
        title: 'Waterproofing and tile',
        body: 'Wet-area membranes, tile surrounds, and flooring details that handle Texas humidity and daily use.',
      },
      {
        title: 'Vanities and fixtures',
        body: 'Single or double vanities, stone tops, toilets, and finish packages sized to the room.',
      },
      {
        title: 'Ventilation and lighting',
        body: 'Ducted exhaust fans and layered lighting planned with electrical access on site.',
      },
    ],
    processTitle: `How a bathroom quote works in ${meta.name}`,
    processSteps: [
      { label: '1. Request', body: `Share your ${meta.name}-area city, hall bath vs primary suite, and conversion goals.` },
      { label: '2. On-site visit', body: 'We measure wet walls, check plumbing access, and discuss waterproofing.' },
      { label: '3. Written estimate', body: 'You receive a written estimate. Free, with no obligation to move forward.' },
    ],
    materialsTitle: `Materials that fit ${meta.name} bathrooms`,
    materialsIntro: 'Bring must-haves and nice-to-haves to the visit so tile, glass, and fixtures can be priced accurately.',
    considerationsTitle: `What ${meta.name} homeowners should decide early`,
    areasTitle: `${meta.name} service area context`,
    areasIntro: `We serve bathroom remodeling requests across ${meta.name} and nearby communities when coverage allows.`,
    nearby: swapNearby('bathroom-remodeling', city),
    ctaTitle: `Ready for a ${meta.name} bathroom quote?`,
    ctaLead: 'Free on-site estimate. No obligation.',
    whyTitle: `Why ${meta.name} homeowners remodel bathrooms`,
    ...unique,
  };
}

function aduCity(city: CityKey, unique: Partial<ServiceCityDeepContent> & Pick<
  ServiceCityDeepContent,
  | 'introParagraphs'
  | 'whyItems'
  | 'costLead'
  | 'timelineLead'
  | 'materials'
  | 'considerations'
  | 'areas'
  | 'faqs'
>): ServiceCityDeepContent {
  const meta = CITY_META[city];
  return {
    serviceSlug: 'adu-construction',
    citySlug: city,
    metaTitle: `ADU Construction in ${meta.name}, TX | 2026 Cost Guide | Epic Home Pros`,
    metaDescription: `${meta.name} ADU estimates with a 2026 Texas cost guide, lot-visit planning, and free written estimates.`,
    eyebrow: `${meta.name}, TX ADU Construction`,
    heroTitle: `ADU Construction in ${meta.name}, TX`,
    heroLead: `Detached cottages, garage conversions, and attached suites in ${meta.region} - start with a free lot visit and written estimate.`,
    introTitle: `ADU planning for ${meta.name} lots`,
    servicesTitle: `ADU services in ${meta.name}`,
    servicesIntro: 'Common paths include the categories below. Feasibility depends on your lot.',
    serviceItems: [
      { title: 'Detached backyard cottages', body: 'New structures planned around setbacks, utilities, and access.' },
      { title: 'Garage conversions', body: 'Insulation, egress, finishes, and utility upgrades inside an existing garage.' },
      { title: 'Attached suites', body: 'Connected living with shared or separate entries when the house allows.' },
      { title: 'Lot evaluation', body: 'Setbacks, utilities, parking, and equipment access reviewed before design locks in.' },
    ],
    processTitle: `How an ADU quote works in ${meta.name}`,
    processSteps: [
      { label: '1. Request', body: `Share your ${meta.name}-area city, lot notes, and conversion vs new-build goals.` },
      { label: '2. Lot visit', body: 'We walk setbacks, utilities, and access to see what is realistic.' },
      { label: '3. Written estimate', body: 'You receive a written estimate. Free, with no obligation to move forward.' },
    ],
    materialsTitle: `ADU decisions for ${meta.name} properties`,
    materialsIntro: 'Choose the structure path before finishing details.',
    considerationsTitle: `What ${meta.name} homeowners should decide early`,
    areasTitle: `${meta.name} service area context`,
    areasIntro: `We serve ADU requests across ${meta.name} and nearby communities when coverage allows.`,
    nearby: swapNearby('adu-construction', city),
    ctaTitle: `Ready for a ${meta.name} ADU quote?`,
    ctaLead: 'Free estimate. No obligation.',
    whyTitle: `Why ${meta.name} homeowners explore ADUs`,
    ...unique,
  };
}

function outdoorCity(city: CityKey, unique: Partial<ServiceCityDeepContent> & Pick<
  ServiceCityDeepContent,
  | 'introParagraphs'
  | 'whyItems'
  | 'costLead'
  | 'timelineLead'
  | 'materials'
  | 'considerations'
  | 'areas'
  | 'faqs'
>): ServiceCityDeepContent {
  const meta = CITY_META[city];
  return {
    serviceSlug: 'outdoor-living',
    citySlug: city,
    metaTitle: `Outdoor Living in ${meta.name}, TX | 2026 Cost Guide | Epic Home Pros`,
    metaDescription: `${meta.name} outdoor living with a 2026 Texas cost guide for decks, covered patios, pergolas, and outdoor kitchens.`,
    eyebrow: `${meta.name}, TX Outdoor Living`,
    heroTitle: `Outdoor Living in ${meta.name}, TX`,
    heroLead: `Decks, covered patios, pergolas, and outdoor kitchens for ${meta.region} weather - free on-site yard estimates.`,
    introTitle: `Outdoor living for ${meta.name} yards`,
    servicesTitle: `Outdoor living services in ${meta.name}`,
    servicesIntro: 'Projects commonly include the categories below. Your written estimate defines scope.',
    serviceItems: [
      { title: 'Decks', body: 'Cedar, composite, and hardwood options sized for sun, slope, and access.' },
      { title: 'Covered patios', body: 'Solid shade and weather cover planned with drainage and house attachments.' },
      { title: 'Pergolas', body: 'Open shade structures with optional lighting and fans.' },
      { title: 'Outdoor kitchens', body: 'Grill islands and counters with gas and electrical planned on site.' },
    ],
    processTitle: `How an outdoor quote works in ${meta.name}`,
    processSteps: [
      { label: '1. Request', body: `Share your ${meta.name}-area city, project type, and rough size.` },
      { label: '2. Yard visit', body: 'We review sun, drainage, access, and how the structure connects to the house.' },
      { label: '3. Written estimate', body: 'You receive a written estimate. Free, with no obligation to move forward.' },
    ],
    materialsTitle: `Materials that hold up in ${meta.name}`,
    materialsIntro: 'Choose products for heat, sun, and maintenance as much as for style.',
    considerationsTitle: `What ${meta.name} homeowners should decide early`,
    areasTitle: `${meta.name} service area context`,
    areasIntro: `We serve outdoor living requests across ${meta.name} and nearby communities when coverage allows.`,
    nearby: swapNearby('outdoor-living', city),
    ctaTitle: `Ready for a ${meta.name} outdoor quote?`,
    ctaLead: 'Free on-site estimate. No obligation.',
    whyTitle: `Why ${meta.name} homeowners build outdoor rooms`,
    ...unique,
  };
}

export const SERVICE_CITY_DEEP: Record<string, Record<string, ServiceCityDeepContent>> = {
  'bathroom-remodeling': {
    austin: bathCity('austin', {
      introParagraphs: [
        'Austin bathrooms range from compact hall baths in older neighborhoods to larger primary suites in newer suburban homes. Waterproofing and ventilation matter in Central Texas humidity.',
        'Homeowners remodel for walk-in showers, tub conversions, better storage, and finishes that hold up to daily use. Scope can stay in the same footprint or expand when plumbing allows.',
        'Use 2026 Texas planning ranges for early budgeting. Your Austin written estimate after an on-site visit is the number for your house.',
        'Epic Home Pros helps Austin-area homeowners start with one request, an on-site visit, and a free written estimate.',
      ],
      whyItems: [
        'Updating dated tile and fixtures in established Central Texas homes',
        'Converting tubs to walk-in showers for easier daily use',
        'Improving ventilation and waterproofing in humid conditions',
        'Expanding primary suites with dual vanities and better lighting',
        'Preparing baths for longer-term living or resale',
      ],
      costLead:
        'Austin projects still land inside statewide planning tiers. Compact hall baths often stay in cosmetic or full-hall ranges, while primary suites climb when walk-in showers, glass, and dual vanities are included.',
      timelineLead:
        'Tile, glass, and inspection timing set many Austin schedules. Cosmetic work can finish in a few weeks; primary and layout-change projects take longer.',
      materials: [
        'Waterproofing approach for showers and wet floors',
        'Tile format and grout choices',
        'Vanity width and countertop material',
        'Shower glass and fixture finishes',
        'Exhaust fan sizing and duct path',
      ],
      considerations: [
        {
          title: 'Older wet walls',
          body: 'Central Austin homes may hide plumbing or moisture issues that only show after demolition.',
        },
        {
          title: 'Humidity control',
          body: 'A ducted exhaust fan protects grout and finishes in Central Texas weather.',
        },
        {
          title: 'Keep or move the drain',
          body: 'Same-layout updates cost less than relocating showers or expanding wet areas.',
        },
      ],
      areas: [
        'Austin proper and nearby suburban communities',
        'Homes where plumbing access should be checked before layout changes',
        'Projects that prioritize waterproofing and ventilation for local humidity',
      ],
      faqs: [
        {
          question: 'How much does a bathroom remodel cost in Austin?',
          answer:
            'Use the 2026 Texas planning tiers: powder rooms about $6k-$18k, cosmetic refreshes $12k-$25k, full hall baths $25k-$50k, primary suites $45k-$90k, and luxury work $90k-$150k+. Tub-to-shower conversions often plan around $8k-$20k alone. Your written estimate prices your house.',
        },
        {
          question: 'How long does an Austin bathroom remodel take?',
          answer:
            'Powder refreshes can take 1-2 weeks. Full hall baths often need 3-5 weeks. Primary and luxury projects commonly take 4-10+ weeks depending on materials and inspections.',
        },
        {
          question: 'Do Austin bathroom remodels need permits?',
          answer:
            'Like-for-like cosmetic swaps may need fewer permits than drain moves, electrical changes, or wet-wall reconfiguration. Requirements vary - discuss scope on site.',
        },
        {
          question: 'Is the estimate free?',
          answer: 'Yes. Free on-site estimate with no obligation.',
        },
      ],
    }),
    dallas: bathCity('dallas', {
      introParagraphs: [
        'Dallas-area baths range from older in-town layouts to larger suburban primary suites. Remodels often update tile, vanities, and showers while confirming plumbing access.',
        'Whether you want a hall-bath refresh or a full primary rebuild, an on-site visit beats a phone-only guess.',
        '2026 Texas planning ranges give early budget context. Waterproofing and plumbing moves are the usual swingers in DFW homes.',
        'Epic Home Pros helps Dallas and DFW homeowners request one quote, schedule a visit, and review a free written estimate.',
      ],
      whyItems: [
        'Updating dated hall baths in established DFW neighborhoods',
        'Building walk-in showers in primary suites',
        'Improving storage with better vanity layouts',
        'Fixing worn tile and fixtures after years of use',
        'Preparing baths for family needs or resale',
      ],
      costLead:
        'Larger DFW primary suites often land in primary or luxury tiers when dual vanities and custom showers are included. Smaller hall baths can stay in cosmetic or full-hall ranges.',
      timelineLead:
        'Specialty tile and glass lead times commonly shape DFW schedules. Full hall baths often fall near the 3-5 week window when materials arrive on time.',
      materials: [
        'Tile and waterproofing systems',
        'Vanity and stone top selections',
        'Shower valves and glass packages',
        'Flooring transitions into adjoining rooms',
        'Ventilation and lighting layers',
      ],
      considerations: [
        {
          title: 'Plumbing in older homes',
          body: 'In-town DFW homes may need more investigation before wet-wall moves.',
        },
        {
          title: 'Primary suite ambitions',
          body: 'Dual vanities and freestanding tubs need clearances confirmed on site.',
        },
        {
          title: 'Family hall baths',
          body: 'Durable tile and practical storage often beat luxury finishes in high-traffic baths.',
        },
      ],
      areas: [
        'Dallas proper and nearby DFW suburbs',
        'Fort Worth and Arlington coverage confirmed by quote address',
        'Homes where older plumbing may affect remodel scope',
      ],
      faqs: [
        {
          question: 'Do you cover Fort Worth and Arlington for bathrooms?',
          answer:
            'Yes when coverage allows. Submit a quote with your address and we will confirm.',
        },
        {
          question: 'How much does a Dallas bathroom remodel cost?',
          answer:
            'Use the 2026 Texas planning tiers on this page. Waterproofing, tile, and plumbing moves drive the number. Your written estimate is house-specific.',
        },
        {
          question: 'Can I convert a tub to a shower in Dallas?',
          answer:
            'Yes. An on-site visit confirms drain location, waterproofing, and whether walls need a full rebuild.',
        },
        {
          question: 'Is the estimate free?',
          answer: 'Yes. Free on-site estimate with no obligation.',
        },
      ],
    }),
    houston: bathCity('houston', {
      introParagraphs: [
        'Houston-area bathrooms benefit from durable finishes and strong ventilation for Gulf Coast humidity. Remodels range from cosmetic updates to full primary suites.',
        'An on-site visit keeps waterproofing, exhaust ducting, and plumbing access grounded in the house.',
        'Use 2026 Texas planning ranges for early budgeting. Your Houston written estimate after the visit is the number for your bath.',
        'Request a quote for your Houston-area address. Free estimates. No obligation.',
      ],
      whyItems: [
        'Updating finishes after years of humidity and daily use',
        'Improving waterproofing in showers and wet floors',
        'Adding walk-in showers and better lighting',
        'Modernizing vanities and storage',
        'Preparing baths for longer-term living',
      ],
      costLead:
        'Humidity-aware materials and full waterproofing packages can push Houston baths toward mid and primary tiers even when the footprint stays the same. Luxury numbers apply when spa features and layout changes enter the plan.',
      timelineLead:
        'Glass and tile lead times still set most calendars. Add time when drains move or custom stone is involved.',
      materials: [
        'Moisture-tolerant tile and waterproofing details',
        'Vanity finishes suited to humid climates',
        'Exhaust fans ducted outdoors',
        'Shower glass and fixture packages',
        'Flooring that handles splashes and traffic',
      ],
      considerations: [
        {
          title: 'Ventilation first',
          body: 'A real outdoor-ducted fan helps protect finishes in Greater Houston humidity.',
        },
        {
          title: 'Waterproofing quality',
          body: 'Wet-area membranes matter more than decorative tile alone.',
        },
        {
          title: 'Suburb coverage',
          body: 'Confirm Pasadena, Pearland, Sugar Land, or The Woodlands coverage with a quote.',
        },
      ],
      areas: [
        'Houston proper and nearby suburbs',
        'Communities such as Pasadena, Pearland, Sugar Land, and The Woodlands - confirm with a quote',
        'Homes where humidity should inform finish and ventilation choices',
      ],
      faqs: [
        {
          question: 'Do you serve Houston suburbs for bathroom remodels?',
          answer:
            'Yes when coverage allows - including communities such as Pasadena, Pearland, Sugar Land, and The Woodlands.',
        },
        {
          question: 'How much does a Houston bathroom remodel cost?',
          answer:
            'Use the 2026 Texas planning tiers on this page. Your written estimate after an on-site visit is the number for your house.',
        },
        {
          question: 'How long does a Houston bathroom remodel take?',
          answer:
            'Cosmetic work can finish in a few weeks. Full hall baths often need 3-5 weeks. Primary and luxury projects commonly take longer.',
        },
        {
          question: 'Is the estimate free?',
          answer: 'Yes. Free on-site estimate with no obligation.',
        },
      ],
    }),
    'san-antonio': bathCity('san-antonio', {
      introParagraphs: [
        'San Antonio bathrooms benefit from durable tile, practical storage, and layouts that fit how the household uses the room.',
        'Projects often start with walk-in showers, vanity updates, and better lighting before larger plumbing moves.',
        '2026 Texas planning ranges help set expectations. Your written estimate prices the bath you actually have.',
        'Epic Home Pros helps San Antonio homeowners start with one request, an on-site visit, and a free written estimate.',
      ],
      whyItems: [
        'Refreshing dated tile and fixtures',
        'Converting tubs to walk-in showers',
        'Improving vanity storage',
        'Updating lighting and ventilation',
        'Making baths easier for daily family use',
      ],
      costLead:
        'Many San Antonio projects start in cosmetic or full-hall tiers when the footprint stays put. Primary and luxury numbers apply when custom showers and premium finishes enter the plan.',
      timelineLead:
        'Same statewide planning windows apply. Confirm tile and glass lead times early if you have a hard completion date.',
      materials: [
        'Tile and waterproofing selections',
        'Vanity styles and tops',
        'Fixture finishes for daily use',
        'Shower glass packages',
        'Exhaust and lighting details',
      ],
      considerations: [
        {
          title: 'Practical durability',
          body: 'Choose tile and fixtures for cleaning and longevity as much as for style.',
        },
        {
          title: 'Family traffic',
          body: 'Hall baths often need durable surfaces and smart storage more than spa features.',
        },
        {
          title: 'Keep the footprint',
          body: 'Many strong remodels stay in the existing layout and put budget into wet-area quality.',
        },
      ],
      areas: [
        'San Antonio proper and surrounding communities',
        'Homes where existing footprints drive realistic shower layouts',
        'Projects that prioritize waterproofing before decorative upgrades',
      ],
      faqs: [
        {
          question: 'How much does a San Antonio bathroom remodel cost?',
          answer:
            'Use the 2026 Texas planning tiers on this page. Your written estimate is specific to your home.',
        },
        {
          question: 'Can you do tub-to-shower conversions in San Antonio?',
          answer:
            'Yes. An on-site visit confirms drain location, waterproofing, and glass options.',
        },
        {
          question: 'Is there an obligation after the estimate?',
          answer: 'No. Estimates are free and you are not obligated to move forward.',
        },
        {
          question: 'How quickly will you respond?',
          answer: 'We aim to reply within one business day after your quote request.',
        },
      ],
    }),
  },

  'adu-construction': {
    austin: aduCity('austin', {
      introParagraphs: [
        'Austin ADU interest is high across varied lot sizes - from compact central parcels to larger suburban yards. Setbacks, utilities, and parking rules should be checked early.',
        'Garage conversions and detached cottages are both common conversation starters. Feasibility depends on the lot, not just the floor plan.',
        '2026 Texas planning ranges help frame budget talks. Your written estimate after a lot visit is the number for your property.',
        'Epic Home Pros helps Austin-area homeowners start with one request, a lot walk when needed, and a free written estimate.',
      ],
      whyItems: [
        'Creating space for family or guests',
        'Exploring garage conversion potential',
        'Planning a detached backyard cottage',
        'Understanding setbacks and utility paths before design',
        'Getting a realistic budget before committing to plans',
      ],
      costLead:
        'Austin ADUs still follow statewide planning ranges. Compact conversions can sit lower; new detached builds often land in the $180,000-$350,000+ planning band depending on size and site work.',
      timelineLead:
        'Design and permitting usually take real calendar time before construction. Utility connections and review cycles commonly extend Austin ADU schedules.',
      materials: [
        'Conversion vs detached path',
        'Foundation and site work needs',
        'Utility connection strategy',
        'Interior finish package level',
        'Access for materials and equipment',
      ],
      considerations: [
        {
          title: 'Setbacks first',
          body: 'A yard that looks large enough can still fail local setback or height checks.',
        },
        {
          title: 'Utilities',
          body: 'Water, sewer, and electric extensions often decide conversion vs new-build cost.',
        },
        {
          title: 'Parking rules',
          body: 'Confirm local parking expectations before locking a footprint.',
        },
      ],
      areas: [
        'Austin proper and nearby communities',
        'Lots where alley or side access affects feasibility',
        'Projects that need early utility and setback review',
      ],
      faqs: [
        {
          question: 'How much does an ADU cost in Austin?',
          answer:
            'Many new detached or attached ADUs plan around $180,000-$350,000+, with larger or higher-finish projects above that. Garage conversions can cost less. Your lot visit sets the real number.',
        },
        {
          question: 'Do Austin ADU rules vary by property?',
          answer:
            'Yes. Setbacks, parking, height, and utilities vary. Walk the lot before locking a plan.',
        },
        {
          question: 'Is the ADU estimate free?',
          answer: 'Yes. Free estimate with no obligation.',
        },
      ],
    }),
    dallas: aduCity('dallas', {
      introParagraphs: [
        'DFW lots range from tight in-town parcels to larger suburban yards. ADU feasibility depends on access, setbacks, and utility paths.',
        'Homeowners explore conversions and new detached units for guests, family, or flexible living. A lot walk beats assumptions.',
        'Use 2026 Texas planning ranges as early context. Your written estimate prices your property.',
        'Epic Home Pros helps Dallas and DFW homeowners request one quote and schedule a free estimate path.',
      ],
      whyItems: [
        'Adding space for multigenerational living',
        'Evaluating garage conversion potential',
        'Planning detached guest cottages',
        'Understanding DFW lot constraints early',
        'Comparing conversion vs new-build budgets',
      ],
      costLead:
        'In-town DFW lots can face tighter access and utility constraints that affect cost. Larger suburban lots may support detached builds more easily when setbacks and utilities allow.',
      timelineLead:
        'Permitting and utility timelines vary across DFW cities. Plan for a multi-month process on most new-build ADUs.',
      materials: [
        'Structure path - conversion, detached, or attached',
        'Site access and grading needs',
        'Utility extension scope',
        'Finish package for kitchenette and bath',
        'Parking and entry planning',
      ],
      considerations: [
        {
          title: 'City-by-city rules',
          body: 'Dallas, Fort Worth, and nearby cities can differ on setbacks and parking.',
        },
        {
          title: 'Access',
          body: 'Side yards and alleys affect whether equipment and materials can reach the build area.',
        },
        {
          title: 'Use case',
          body: 'Guest lodging vs longer-term family use changes layout priorities.',
        },
      ],
      areas: [
        'Dallas proper and nearby DFW communities',
        'Fort Worth and Arlington coverage confirmed by quote',
        'Lots where utility and access notes should come first',
      ],
      faqs: [
        {
          question: 'Do you cover Fort Worth ADU quotes?',
          answer: 'Yes when coverage allows. Submit your address and we will confirm.',
        },
        {
          question: 'How much does a Dallas ADU cost?',
          answer:
            'Use the 2026 Texas planning ranges on this page. Size, utilities, and site work drive the number. A lot visit sets the estimate.',
        },
        {
          question: 'Is the estimate free?',
          answer: 'Yes. Free estimate with no obligation.',
        },
      ],
    }),
    houston: aduCity('houston', {
      introParagraphs: [
        'Greater Houston ADU planning often includes drainage and utility access alongside setbacks. Lot conditions matter as much as floor plans.',
        'Garage conversions and detached units are both worth comparing when the property allows.',
        '2026 Texas planning ranges help with early budgeting. Your written estimate follows a lot visit.',
        'Request a quote for your Houston-area address. Free estimates. No obligation.',
      ],
      whyItems: [
        'Creating flexible living for family',
        'Evaluating conversion potential on existing structures',
        'Planning detached backyard units',
        'Accounting for drainage and utility realities',
        'Getting a realistic path before design spend',
      ],
      costLead:
        'Drainage corrections and utility runs can move Houston ADU budgets even when the floor plan looks simple. Use statewide tiers as context, then price the lot.',
      timelineLead:
        'Weather, permitting, and utility scheduling can extend Greater Houston timelines. Build buffer into the calendar.',
      materials: [
        'Conversion vs new-build path',
        'Drainage and site prep needs',
        'Utility connection strategy',
        'Moisture-aware exterior and interior finishes',
        'Access for construction on suburban lots',
      ],
      considerations: [
        {
          title: 'Drainage',
          body: 'Water management should be part of ADU feasibility in Greater Houston.',
        },
        {
          title: 'Utility access',
          body: 'Confirm water, sewer, and electric paths before locking a footprint.',
        },
        {
          title: 'Suburb rules',
          body: 'Nearby cities may differ - confirm coverage and requirements with a quote.',
        },
      ],
      areas: [
        'Houston proper and nearby suburbs',
        'Communities such as Pasadena, Pearland, Sugar Land, and The Woodlands - confirm with a quote',
        'Lots where drainage and utilities need early review',
      ],
      faqs: [
        {
          question: 'Do you serve Houston suburbs for ADUs?',
          answer:
            'Yes when coverage allows. Include your city on the quote form to confirm.',
        },
        {
          question: 'How much does a Houston ADU cost?',
          answer:
            'Many new builds plan around $180,000-$350,000+, with site work and finishes moving the number. Conversions can cost less. A lot visit sets the estimate.',
        },
        {
          question: 'Is the estimate free?',
          answer: 'Yes. Free estimate with no obligation.',
        },
      ],
    }),
    'san-antonio': aduCity('san-antonio', {
      introParagraphs: [
        'San Antonio ADU projects usually start with a realistic lot walk before finish selections. Setbacks and utilities decide more than mood boards.',
        'Homeowners compare garage conversions and detached cottages for guests or family. Feasibility is property-specific.',
        'Use 2026 Texas planning ranges for early context. Your written estimate prices your lot.',
        'Epic Home Pros helps San Antonio homeowners start with one request and a free estimate path.',
      ],
      whyItems: [
        'Adding flexible living space',
        'Evaluating garage conversion options',
        'Planning detached guest cottages',
        'Understanding lot constraints early',
        'Setting a realistic budget before design',
      ],
      costLead:
        'Many San Antonio ADUs follow statewide planning bands. Site work and utility connections usually matter more than interior finish choices at the start.',
      timelineLead:
        'Expect design and permitting time before construction. Confirm local review expectations during the lot visit.',
      materials: [
        'Structure path and footprint',
        'Utility strategy',
        'Foundation and site needs',
        'Interior finish level',
        'Entry and privacy planning',
      ],
      considerations: [
        {
          title: 'Lot first',
          body: 'Confirm setbacks and utilities before choosing finishes.',
        },
        {
          title: 'Use case',
          body: 'Family guests vs longer stays change kitchenette and bath priorities.',
        },
        {
          title: 'Access',
          body: 'Make sure equipment and materials can reach the build area.',
        },
      ],
      areas: [
        'San Antonio proper and surrounding communities',
        'Lots where setbacks and utilities need early confirmation',
        'Projects comparing conversion vs new-build paths',
      ],
      faqs: [
        {
          question: 'How much does a San Antonio ADU cost?',
          answer:
            'Use the 2026 Texas planning ranges on this page. Your lot visit sets the written estimate.',
        },
        {
          question: 'Garage conversion or new build?',
          answer:
            'Conversions can involve less foundation work but still need insulation, egress, and utilities. Compare both for your lot.',
        },
        {
          question: 'Is the estimate free?',
          answer: 'Yes. Free estimate with no obligation.',
        },
      ],
    }),
  },

  'outdoor-living': {
    austin: outdoorCity('austin', {
      introParagraphs: [
        'Austin yards often prioritize shade and heat-aware materials for long summers. Decks, covered patios, pergolas, and outdoor kitchens all start with how you use the space.',
        'An on-site walk keeps sun, drainage, and access grounded in the lot - not a catalog photo.',
        'Use 2026 Texas planning ranges for early budgeting. Your Austin written estimate prices your yard.',
        'Epic Home Pros helps Austin-area homeowners start with one request and a free on-site estimate.',
      ],
      whyItems: [
        'Adding usable shade for Texas summers',
        'Building decks that handle heat and sun',
        'Covering patios for rain and harsh light',
        'Planning outdoor kitchens for entertaining',
        'Improving backyard flow from indoor living spaces',
      ],
      costLead:
        'Simple Austin decks can land near the $8,000-$20,000 planning band depending on size and material. Covered structures and outdoor kitchens climb when roof systems and utilities are added.',
      timelineLead:
        'Weather and material lead times shape Austin outdoor schedules. Utility work for kitchens extends the calendar.',
      materials: [
        'Cedar, composite, or hardwood decking',
        'Pergola vs solid covered patio',
        'Railing and stair details',
        'Outdoor-rated lighting',
        'Gas and electrical needs for kitchens',
      ],
      considerations: [
        {
          title: 'Shade first',
          body: 'A beautiful deck that bakes in full sun often goes unused in summer.',
        },
        {
          title: 'Heat underfoot',
          body: 'Ask how composite or dark boards feel in direct Austin sun.',
        },
        {
          title: 'Drainage',
          body: 'Keep water moving away from the house and outdoor footings.',
        },
      ],
      areas: [
        'Austin proper and nearby communities',
        'Yards where slope and access affect structure choices',
        'Projects that need shade and heat planning for Central Texas',
      ],
      faqs: [
        {
          question: 'How much does outdoor living cost in Austin?',
          answer:
            'Composite decks often plan around $8,000-$20,000 depending on size. Covered patios and outdoor kitchens climb from there. Your written estimate prices the yard.',
        },
        {
          question: 'What outdoor projects do you handle in Austin?',
          answer:
            'Decks, covered patios, pergolas, outdoor kitchens, and combined outdoor rooms - scoped after a site walk.',
        },
        {
          question: 'Is the estimate free?',
          answer: 'Yes. Free on-site estimate with no obligation.',
        },
      ],
    }),
    dallas: outdoorCity('dallas', {
      introParagraphs: [
        'DFW outdoor rooms need sun planning and storm-ready detailing for North Texas weather. Decks, covers, and kitchens should fit how the household entertains.',
        'A yard visit confirms drainage, access, and attachment details before materials are locked.',
        '2026 Texas planning ranges help with early budgeting. Your written estimate prices the site.',
        'Epic Home Pros helps Dallas and DFW homeowners start with one request and a free estimate.',
      ],
      whyItems: [
        'Creating shaded entertaining space',
        'Replacing worn decks and covers',
        'Adding outdoor kitchens for gatherings',
        'Improving backyard flow from the kitchen',
        'Choosing materials for North Texas weather',
      ],
      costLead:
        'DFW deck projects often follow statewide deck ranges, then rise with elevation, railings, and solid covers. Outdoor kitchens add gas and electrical cost.',
      timelineLead:
        'Material lead times and weather windows commonly shape DFW outdoor schedules. Combined outdoor rooms take longer than a single deck.',
      materials: [
        'Deck material for sun and maintenance goals',
        'Covered patio roof systems',
        'Pergola shade options',
        'Outdoor kitchen utility needs',
        'Railings, stairs, and lighting',
      ],
      considerations: [
        {
          title: 'Sun orientation',
          body: 'Afternoon west sun can make shade more valuable than extra deck square footage.',
        },
        {
          title: 'Storm detailing',
          body: 'Attachments and fasteners should be planned for North Texas weather.',
        },
        {
          title: 'Suburb coverage',
          body: 'Confirm Fort Worth or Arlington coverage with a quote.',
        },
      ],
      areas: [
        'Dallas proper and nearby DFW suburbs',
        'Fort Worth and Arlington coverage confirmed by quote',
        'Yards where sun and drainage should guide structure choice',
      ],
      faqs: [
        {
          question: 'Do you cover Fort Worth outdoor projects?',
          answer: 'Yes when coverage allows. Submit your address to confirm.',
        },
        {
          question: 'How much does a Dallas outdoor project cost?',
          answer:
            'Use the 2026 Texas planning ranges on this page. Size, cover, and utilities drive the number.',
        },
        {
          question: 'Is the estimate free?',
          answer: 'Yes. Free on-site estimate with no obligation.',
        },
      ],
    }),
    houston: outdoorCity('houston', {
      introParagraphs: [
        'Greater Houston outdoor projects should account for humidity, drainage, and summer storms. Shade and durable materials decide whether the space gets used.',
        'An on-site walk reviews water flow, sun, and access before pricing decks, covers, or kitchens.',
        'Use 2026 Texas planning ranges for early context. Your written estimate prices the yard.',
        'Request a quote for your Houston-area address. Free estimates. No obligation.',
      ],
      whyItems: [
        'Building shade for humid summers',
        'Improving drainage around outdoor structures',
        'Adding covered entertaining space',
        'Planning outdoor kitchens with proper utilities',
        'Choosing materials that handle Gulf Coast conditions',
      ],
      costLead:
        'Houston outdoor budgets often rise when drainage corrections and solid covers are required. Decks can still start near statewide planning bands when the site is straightforward.',
      timelineLead:
        'Weather and ground conditions can extend Greater Houston outdoor schedules. Utility work for kitchens adds more calendar time.',
      materials: [
        'Moisture-tolerant decking options',
        'Covered patio systems for rain and sun',
        'Pergola shade with optional fans',
        'Outdoor kitchen utility planning',
        'Drainage and site prep details',
      ],
      considerations: [
        {
          title: 'Drainage first',
          body: 'Standing water near footings and the house should be solved in the plan.',
        },
        {
          title: 'Humidity and materials',
          body: 'Choose products and detailing that hold up in Gulf Coast conditions.',
        },
        {
          title: 'Suburb coverage',
          body: 'Confirm nearby cities such as Sugar Land or The Woodlands with a quote.',
        },
      ],
      areas: [
        'Houston proper and nearby suburbs',
        'Communities such as Pasadena, Pearland, Sugar Land, and The Woodlands - confirm with a quote',
        'Yards where drainage and shade should lead the design',
      ],
      faqs: [
        {
          question: 'Do you serve Houston suburbs for outdoor living?',
          answer:
            'Yes when coverage allows. Include your city on the quote form.',
        },
        {
          question: 'How much does Houston outdoor living cost?',
          answer:
            'Use the 2026 Texas planning ranges on this page. Covers and utilities usually move the number more than deck boards alone.',
        },
        {
          question: 'Is the estimate free?',
          answer: 'Yes. Free on-site estimate with no obligation.',
        },
      ],
    }),
    'san-antonio': outdoorCity('san-antonio', {
      introParagraphs: [
        'San Antonio outdoor living often focuses on shade, durable surfaces, and evening comfort. Decks, pergolas, and covered patios should fit how the household gathers.',
        'A yard visit confirms sun, drainage, and access before material selections lock in.',
        '2026 Texas planning ranges help set expectations. Your written estimate prices the site.',
        'Epic Home Pros helps San Antonio homeowners start with one request and a free on-site estimate.',
      ],
      whyItems: [
        'Adding shade for South Texas sun',
        'Creating evening entertaining space',
        'Updating worn decks and covers',
        'Planning outdoor kitchens for gatherings',
        'Improving indoor-outdoor flow',
      ],
      costLead:
        'Many San Antonio decks follow statewide planning bands. Covered patios and outdoor kitchens climb when roof systems and utilities are included.',
      timelineLead:
        'Confirm material lead times early if you are planning around a gathering date. Combined outdoor rooms take longer than a single structure.',
      materials: [
        'Deck material for heat and maintenance',
        'Pergola or solid cover choice',
        'Lighting for evening use',
        'Outdoor kitchen utility needs',
        'Railings and stair details',
      ],
      considerations: [
        {
          title: 'Shade and evening use',
          body: 'Plan for both afternoon sun and nighttime gatherings.',
        },
        {
          title: 'Durable finishes',
          body: 'Choose surfaces that handle South Texas heat and daily use.',
        },
        {
          title: 'Keep the plan practical',
          body: 'A well-shaded smaller structure often beats an oversized uncovered deck.',
        },
      ],
      areas: [
        'San Antonio proper and surrounding communities',
        'Yards where sun orientation should guide shade design',
        'Projects comparing decks, covers, and outdoor kitchens',
      ],
      faqs: [
        {
          question: 'How much does outdoor living cost in San Antonio?',
          answer:
            'Use the 2026 Texas planning ranges on this page. Your written estimate after a yard visit is the number for your lot.',
        },
        {
          question: 'What outdoor projects are available in San Antonio?',
          answer:
            'Decks, covered patios, pergolas, outdoor kitchens, and combined outdoor rooms - scoped after a site walk.',
        },
        {
          question: 'Is the estimate free?',
          answer: 'Yes. Free on-site estimate with no obligation.',
        },
      ],
    }),
  },
};

export function getServiceCityDeep(
  serviceSlug: string,
  citySlug: string
): ServiceCityDeepContent | undefined {
  return SERVICE_CITY_DEEP[serviceSlug]?.[citySlug];
}
